import type { FindingEntry } from '../api/client';
import { BAND_RANK, reportedBand } from './vocabulary';

/**
 * The order a reviewer reads findings in — one derivation, two consumers
 * (docs/DECISIONS.md ZD-123 §11 defect 1, closed in ZD-124 §5).
 *
 * The first live run showed the list's highlighted row and the detail pane
 * disagreeing until a finding was clicked: the page seeded its selection from
 * the API's order while the list sorted by band before rendering, so a
 * reviewer reading without clicking judged one finding by another's evidence.
 * The list and the initial selection now read this one ordering, so the
 * first row a person sees is the finding the pane shows.
 *
 * Strongest first, then by title; an unbanded finding sorts last rather than
 * first, because it is not stronger than everything — it is unranked.
 */
export function orderFindings(findings: readonly FindingEntry[]): FindingEntry[] {
  return [...findings].sort((a, b) => {
    const bandA = reportedBand(a.finding);
    const bandB = reportedBand(b.finding);
    const rankA = bandA === null ? Number.MAX_SAFE_INTEGER : BAND_RANK[bandA];
    const rankB = bandB === null ? Number.MAX_SAFE_INTEGER : BAND_RANK[bandB];
    if (rankA !== rankB) return rankA - rankB;
    return a.finding.title.localeCompare(b.finding.title);
  });
}

/** The finding the page opens on: the first row a person sees, or `null` where there is none. */
export function initialSelection(findings: readonly FindingEntry[]): string | null {
  return orderFindings(findings)[0]?.finding.finding_id ?? null;
}
